/**
 * I COLORI DEL MARCHIO: UNA GRAMMATICA SOLA, E NEL CSS SOLO TRE NUMERI.
 *
 * Il backend usa `parseColor` per rifiutare (422), il web per convertire. Un
 * parser solo, perche' due parser divergono: quello del web accetterebbe un
 * giorno qualcosa che quello del backend rifiuta, o peggio il contrario.
 *
 * **Nel CSS non entra mai la stringa scritta**: il web scrive sulle variabili
 * soltanto il risultato di `colorToRgbTriplet`, cioe' tre interi fra 0 e 255.
 * `red; background:url(…)`, `var(--x)`, `expression(…)` non passano la
 * grammatica, e anche se la passassero non arriverebbero al foglio di stile.
 *
 * Accettati, e nient'altro (niente trasparenza: i token la mettono da se'):
 *   #rgb  #rrggbb
 *   rgb(r g b)       rgb(r, g, b)         interi 0–255
 *   hsl(h s% l%)     hsl(h, s%, l%)       h 0–360 (anche `deg`), s e l 0–100
 *   oklch(L C H)                          L 0–1 o 0%–100%, C 0–0.5, H 0–360
 */
/**
 * Le chiavi di `Tenant.brandingJson`, che sono i nomi delle variabili di
 * `apps/web/src/index.css` senza `--`: `applyBranding` scrive `--<chiave>`.
 */
export const BRANDING_COLOR_KEYS = [
    "accent",
    "on-accent",
    "page",
    "surface",
    "raised",
    "secondary",
    "muted",
    "line",
    "sidebar",
    "strong",
];
/** Oltre, e' un testo che non ha niente a che fare con un colore. */
export const BRANDING_COLOR_MAX_LENGTH = 64;
const NUM = String.raw `(\d{1,3}(?:\.\d{1,6})?|\.\d{1,6})`;
const SEP = String.raw `\s*(?:,\s*|\s+)`;
const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_RE = new RegExp(String.raw `^rgb\(\s*(\d{1,3})${SEP}(\d{1,3})${SEP}(\d{1,3})\s*\)$`, "i");
const HSL_RE = new RegExp(String.raw `^hsl\(\s*${NUM}(?:deg)?${SEP}${NUM}%${SEP}${NUM}%\s*\)$`, "i");
const OKLCH_RE = new RegExp(String.raw `^oklch\(\s*${NUM}(%?)\s+${NUM}\s+${NUM}(?:deg)?\s*\)$`, "i");
const clamp01 = (x) => Math.min(1, Math.max(0, x));
const to255 = (x) => Math.round(clamp01(x) * 255);
/** Il colore letto, o `null` se il testo non e' esattamente uno dei formati accettati. */
export function parseColor(input) {
    if (typeof input !== "string")
        return null;
    const s = input.trim();
    if (!s || s.length > BRANDING_COLOR_MAX_LENGTH)
        return null;
    let m = HEX_RE.exec(s);
    if (m) {
        const h = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
        const n = parseInt(h, 16);
        return { rgb: [(n >> 16) & 255, (n >> 8) & 255, n & 255], clipped: false };
    }
    m = RGB_RE.exec(s);
    if (m) {
        const c = [Number(m[1]), Number(m[2]), Number(m[3])];
        if (c.some((v) => v > 255))
            return null;
        return { rgb: c, clipped: false };
    }
    m = HSL_RE.exec(s);
    if (m) {
        const h = Number(m[1]);
        const sat = Number(m[2]);
        const l = Number(m[3]);
        if (h > 360 || sat > 100 || l > 100)
            return null;
        return { rgb: hslToRgb(h, sat / 100, l / 100), clipped: false };
    }
    m = OKLCH_RE.exec(s);
    if (m) {
        const pct = m[2] === "%";
        const L = Number(m[1]) / (pct ? 100 : 1);
        const C = Number(m[3]);
        const H = Number(m[4]);
        if (L > 1 || C > 0.5 || H > 360)
            return null;
        return oklchToRgb(L, C, H);
    }
    return null;
}
/** «R G B» per le variabili CSS (`rgb(var(--x) / <alpha>)`), o `null` se il colore non e' valido. */
export function colorToRgbTriplet(input) {
    const c = parseColor(input);
    return c ? c.rgb.join(" ") : null;
}
function hslToRgb(h, s, l) {
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [to255(f(0)), to255(f(8)), to255(f(4))];
}
/** OKLCH -> sRGB (matrici di Björn Ottosson). Fuori gamma si avvicina, e lo si dice. */
function oklchToRgb(L, C, H) {
    const hr = (H * Math.PI) / 180;
    const a = C * Math.cos(hr);
    const b = C * Math.sin(hr);
    const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
    const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
    const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
    const lin = [
        4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    ];
    const EPS = 1e-4;
    const clipped = lin.some((v) => v < -EPS || v > 1 + EPS);
    const gamma = (x) => {
        const v = clamp01(x);
        return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
    };
    return { rgb: [to255(gamma(lin[0])), to255(gamma(lin[1])), to255(gamma(lin[2]))], clipped };
}
//# sourceMappingURL=color.js.map